import { useState } from 'react';
import { Trash2 } from 'lucide-react';
import { addUser, removeUser } from '../../../utils/demoEnvMgmtApi';
import { errorMessage } from './error-message';
import { formatUserLabel } from '../label-format';
import ConfirmDeleteDialog from '../../../components/admin/ConfirmDeleteDialog';

function UsersSection({ users, accounts, onChanged }) {
  const [email, setEmail] = useState('');
  const [accountId, setAccountId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const hasAccounts = accounts.length > 0;

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    if (!accountId) {
      setError('Select an account for this user — add one above if none exist yet.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await addUser({ email: email.trim(), demoEnvAccountId: Number(accountId) });
      setEmail('');
      setAccountId('');
      await onChanged();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteConfirm = async () => {
    setError(null);
    setSubmitting(true);
    try {
      await removeUser(deleteTarget.id);
      setDeleteTarget(null);
      await onChanged();
    } catch (err) {
      setError(errorMessage(err));
      setDeleteTarget(null);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="demo-env-mgmt-section">
      <h2 className="demo-env-mgmt-section-title">Users</h2>
      <p className="demo-env-mgmt-label">
        Predefined ContentSquare service-account login emails, each linked 1-to-1 to an Account
        above. Has no label of its own — shown by its linked account's label everywhere else.
        Selectable in the Entities tab's Email field once linked. The password is still typed
        manually there and never stored.
      </p>

      <div className="demo-env-mgmt-columns" style={{ marginTop: 8 }}>
        <div className="demo-env-mgmt-column">
          <h3 className="demo-env-mgmt-column-title">Add user</h3>
          {!hasAccounts && (
            <p className="demo-env-mgmt-label" style={{ marginBottom: 6 }}>
              Add an account above before adding a user — every user must be linked to one.
            </p>
          )}
          <form onSubmit={handleAdd}>
            <div className="demo-env-mgmt-field">
              <label className="demo-env-mgmt-label" htmlFor="new-user-email">Email</label>
              <input
                id="new-user-email"
                className="demo-env-mgmt-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <div className="demo-env-mgmt-field" style={{ marginTop: 8 }}>
              <label className="demo-env-mgmt-label" htmlFor="new-user-account">Account</label>
              <select
                id="new-user-account"
                className="demo-env-mgmt-select"
                value={accountId}
                onChange={(e) => setAccountId(e.target.value)}
                required
              >
                <option value="">{hasAccounts ? 'Select an account…' : 'No accounts yet'}</option>
                {accounts.map((account) => (
                  <option key={account.id} value={String(account.id)}>{account.label}</option>
                ))}
              </select>
            </div>
            <div className="demo-env-mgmt-field demo-env-mgmt-field-actions" style={{ marginTop: 8 }}>
              <button className="demo-env-mgmt-btn" type="submit" disabled={submitting || !hasAccounts}>
                {submitting ? 'Adding…' : 'Add'}
              </button>
            </div>
          </form>
          {error && <p className="demo-env-mgmt-error">{error}</p>}
        </div>

        <div className="demo-env-mgmt-column">
          <h3 className="demo-env-mgmt-column-title">Users</h3>
          <div className="demo-env-mgmt-table-wrap">
            <table className="demo-env-mgmt-table">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Account</th>
                  <th className="demo-env-mgmt-table-actions" />
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.id}>
                    <td>{user.email}</td>
                    <td>{user.account ? user.account.label : '—'}</td>
                    <td className="demo-env-mgmt-table-actions">
                      <button
                        type="button"
                        className="demo-env-mgmt-btn-delete"
                        onClick={() => setDeleteTarget(user)}
                        disabled={submitting}
                      >
                        <Trash2 size={13} /> Delete
                      </button>
                    </td>
                  </tr>
                ))}
                {users.length === 0 && (
                  <tr className="demo-env-mgmt-empty-row">
                    <td colSpan={3}>No users yet.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {deleteTarget && (
        <ConfirmDeleteDialog
          entityType="user"
          entityName={formatUserLabel(deleteTarget)}
          onConfirm={handleDeleteConfirm}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
    </div>
  );
}

export default UsersSection;
